import React, { useState, useCallback, useContext, useEffect } from "react";
import filter from "lodash/filter";
import { toast } from "react-toastify";
import { getFarms, updateFarm } from "api/Farm";
import { getOrders } from "api/Orders";
import { getBookings } from "api/RapidEM";
import { getContactDetails } from "api/User";
import { UserContext } from "context/user";
import { Leaf, ArchiveBox, CalendarCheck } from "phosphor-react";
import { useHistory } from "react-router-dom";
import { StripeActions, StripeContext } from "context/stripe";
import { getAdvancedPricing } from "api/Stripe";
import StarredFarms from "../components/StarredFarms";
import DashboardPanel from "../components/DashboardPanel";
// import OffersPanel from "../components/OffersPanel";
import WelcomeHeader from "../components/WelcomeHeader";
import PremiumStatus from "../components/PremiumStatus";
import "./index.scss";
import Guides from "../components/Guides";
// import AlertPanel from "../components/AlertPanel";
import SummaryPanel from "../components/SummaryPanel";
// import TopFarmsPanel from "../components/TopFarmsPanel";
// import FeaturedFarmPanel from "../components/FeaturedFarmPanel";
import TutorialVideoPanel from "../components/TutorialVideoPanel";

const DashboardContainer = () => {
  const history = useHistory();
  const { token, user, profile } = useContext(UserContext);
  const [starredFarm, setStarredFarm] = useState([]);
  const [totalFarms, setTotalFarms] = useState(0);
  const [totalBookings, setTotalBookings] = useState(0);
  const [farms, setFarms] = useState([]);
  const [totalProcessingOrders, setTotalProcessingOrders] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const userEmail = profile ? profile.account.email.split("@") : "";
  const nickname = userEmail[0];

  const { dispatch } = useContext(StripeContext);

  const fetchPrice = useCallback(async () => {
    if (!token) return;
    dispatch({
      type: StripeActions.FETCH_PRICING_DETAILS,
    });
    try {
      const payload = await getAdvancedPricing(token);
      dispatch({
        type: StripeActions.FETCH_PRICING_DETAILS_SUCCESSFUL,
        value: payload,
      });
    } catch (error) {
      dispatch({
        type: StripeActions.FETCH_PRICING_DETAILS_FAIL,
      });
    }
  }, [dispatch, token]);

  const fetchFarm = useCallback(async () => {
    const filtered = await getFarms("", token);
    const sortedFarms = filter(
      filtered?.features,
      (farm) => farm?.properties?.is_favorite
    );
    const numOfFields = filter(
      filtered?.features,
      (farm) => farm?.properties?.number_of_fields !== 0
    );
    setStarredFarm(sortedFarms);
    setFarms(numOfFields);
    setTotalFarms(filtered?.pages?.total_count);
  }, [token]);

  const fetchStarredFarms = useCallback(async () => {
    const starredFarms = await getFarms("&is_favorite=true", token);
    setStarredFarm(starredFarms?.features);
  }, [token]);

  const fetchTotalProcessing = useCallback(async () => {
    const query = "statuses=processing&statuses=submitted";
    const orderList = await getOrders(query, token);
    setTotalProcessingOrders(orderList?.pages?.total_count);
  }, [token]);

  const fetchBookings = useCallback(async () => {
    const bookings = await getBookings("", token);
    setTotalBookings(bookings?.data.length);
  }, [token]);

  // for future implementation
  // const fetchOrders = useCallback(async () => {
  //   const orderList = await getOrders("", token);
  //   setOrders(orderList?.pages?.total_count);
  // }, [token]);

  const fetchContactDetails = useCallback(async () => {
    if (!token) return;

    const userData = await getContactDetails(token);
    setUserDetails(userData.data);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchFarm();
    fetchTotalProcessing();
    fetchPrice();
    fetchContactDetails();
    fetchBookings();
    fetchStarredFarms();
  }, [
    token,
    fetchFarm,
    fetchTotalProcessing,
    fetchPrice,
    fetchContactDetails,
    fetchBookings,
    fetchStarredFarms,
  ]);

  const updateFavoriteStatus = async (farm) => {
    try {
      const params = {
        data: {
          farm_name: farm?.farm_name,
          is_account_farm_favorite: !farm?.is_favorite,
        },
      };
      await updateFarm(farm?.farm_id, params, token);
      fetchFarm();
    } catch (e) {
      toast.error(e);
    }
  };

  useEffect(() => {
    if (!profile) return;
    if (!profile?.account?.terms_accepted_version && user?.email_verified) {
      history.push("/getting-started/terms-and-conditions");
    } else if (
      !profile?.account?.terms_accepted_version &&
      !user?.email_verified
    ) {
      history.push("/getting-started/profile-setup");
    }
  }, [history, profile, user]);

  // for future implementation
  // const highresData = [
  //   {
  //     id: "upcoming",
  //     label: "Processing Orders:",
  //     count: totalProcessingOrders || 0,
  //   },
  //   {
  //     id: "pending",
  //     label: "Pending Payments:",
  //     count: totalPendingOrders || 0,
  //   },
  // ];
  // For future implementation
  // const farmsManageData = [
  //   {
  //     id: "farminvites",
  //     label: "Collaboration Invites:",
  //     count: totalFarms || 0,
  //   },
  // ];

  // const farmAlerts = [
  //   {
  //     id: 0,
  //     message: "Pest has been detected in your fields.",
  //     type: "farms",
  //   },
  //   {
  //     id: 1,
  //     message: "High Resolution orders has been delivered.",
  //     type: "order",
  //   },
  // ];

  return (
    <div data-testid="dashboard-page" className="dashboard-page">
      <div className="left-panel">
        <WelcomeHeader user={userDetails} nickname={nickname} />
        <div className="horizontal-divider" />
        <StarredFarms
          farms={starredFarm}
          setFavorite={(e) => updateFavoriteStatus(e)}
          redirectTo={() =>
            history.push({
              pathname: "/farms",
              state: { filter: "favourites" },
            })
          }
        />
        <div className="horizontal-divider" />
        <PremiumStatus />
      </div>
      <div className="right-panel">
        <div className="header">
          <h1 className="headings">Growing. With Greater Certainty.</h1>
          <p className="header-details">
            Welcome to your personal dashboard, where you can find an
            introduction to how Datafarming works, tools to help you build your
            farms, and order your first high resolution imagery.
          </p>
        </div>
        {/* <AlertPanel
          label="Farm Alerts"
          icon={<Broadcast size={24} color="#8A8E99" />}
          description={farmAlerts}
        /> */}
        {totalFarms ? (
          <>
            <SummaryPanel
              totalFarms={totalFarms}
              farms={farms}
              redirectTo={() => history.push("/farms")}
            />
            <section className="group-section">
              <div className="group-panel">
                <DashboardPanel
                  label="Upcoming Orders"
                  icon={<ArchiveBox size={24} color="#8A8E99" />}
                  mainCount={totalProcessingOrders || 0}
                  mainTitle=""
                  redirectTo={() =>
                    history.push({
                      pathname: "/order",
                      state: { tab: "orders" },
                    })
                  }
                  footerLink="order"
                />
                <DashboardPanel
                  label="Upcoming Bookings"
                  icon={<CalendarCheck size={24} color="#8A8E99" />}
                  mainCount={totalBookings}
                  mainTitle=""
                  redirectTo={() =>
                    history.push({
                      pathname: "/order",
                      state: { tab: "em-booking" },
                    })
                  }
                  footerLink="order"
                />
              </div>
              {/* <TopFarmsPanel /> */}
            </section>
          </>
        ) : (
          <DashboardPanel
            label="Let's grow together"
            mainCount="Don't know what to do?"
            description="Start by creating your very first farm and your fields."
            icon={<Leaf size={24} color="#8A8E99" />}
            withButtons
          />
        )}
        {/* <OffersPanel
          label="Available offers"
          icon={<Confetti size={24} color="#8A8E99" />}
          mainTitle=""
          redirectTo={() => history.push("/order")}
        /> */}
        {/* {totalFarms ? <FeaturedFarmPanel label="Toowoomba Farm" /> : null} */}
        <Guides farms={totalFarms} />
        <TutorialVideoPanel />
      </div>
    </div>
  );
};

DashboardContainer.propTypes = {};

export default DashboardContainer;
